import { AdvancedSelect } from '@orobix/orobix-fe-components'
import { FieldArrayRenderProps, useField, useFormikContext } from 'formik'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ProductType } from '../../../types'
import { SelectOption } from './types'
import { usePrebuiltClassesData } from './usePrebuiltClassesData'
import { prebuiltClassesToOption } from './utils'

export interface PrebuiltClassesSelectProps {
  fieldName: string
  productType: ProductType
  props: FieldArrayRenderProps
  min: number
  max: number
}

const PrebuiltClassesSelect = ({
  fieldName,
  productType,
  props,
  min,
  max,
}: PrebuiltClassesSelectProps) => {
  const { t } = useTranslation()
  const { isSubmitting } = useFormikContext()
  const [field, meta, helpers] = useField<SelectOption>(fieldName)

  const { name, value } = field
  const { error, touched, initialValue } = meta
  const { setValue, setTouched } = helpers

  const { prebuiltClasses, isLoading } = usePrebuiltClassesData(productType)

  const toOption = prebuiltClassesToOption()

  useEffect(() => {
    if (value && value.value !== '') {
      enableDisableClassesFields(true)
    } else {
      enableDisableClassesFields(false)
    }
  }, [value])

  function enableDisableClassesFields(disabled: boolean) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 10; i++) {
      const inputSelector = `#classes\\.${i}`
      const inputField = document.querySelector<HTMLInputElement>(inputSelector)
      if (inputField) {
        inputField.disabled = disabled
      }
    }

    const removeButtonSelector = '[data-testid=removeClassButton]'
    const removeButtons = document.querySelectorAll<HTMLButtonElement>(
      removeButtonSelector
    )
    if (removeButtons) {
      removeButtons.forEach((removeButton) => {
        // eslint-disable-next-line no-param-reassign
        if (disabled) {
          // eslint-disable-next-line no-param-reassign
          removeButton.style.display = 'none'
        } else {
          // eslint-disable-next-line no-param-reassign
          removeButton.style.display = 'unset'
        }
      })
    }

    const addButtonSelector = '[data-testid=addClassButton]'
    const addClassButton = document.querySelectorAll<HTMLButtonElement>(
      addButtonSelector
    )[0]
    if (addClassButton) {
      if (disabled) {
        addClassButton.style.display = 'none'
      } else {
        addClassButton.style.display = 'unset'
      }
    }

    const fieldInfoSelector = '[data-testid=fieldsetFeedbackInfo]'
    const fiedlInfo = document.querySelectorAll<HTMLDivElement>(
      fieldInfoSelector
    )[0]
    if (fiedlInfo) {
      if (disabled) {
        fiedlInfo.style.display = 'none'
      } else {
        fiedlInfo.style.display = 'unset'
      }
    }
  }

  function onChangeSelectOption(v: SelectOption | null) {
    if (v) {
      setValue(v)
      removeProps()
      getPrebuiltClassesBySelectedOption(v).forEach((clazz) =>
        props.push(clazz)
      )
      // enableDisableClassesFields(true)
    } else {
      clearSelectedOption()
    }
  }

  function clearSelectedOption() {
    setValue({ value: '', label: t('Select...') })
    createDefaultProps()
  }

  function createDefaultProps() {
    removeProps()
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < min; i++) {
      props.push('')
    }
    enableDisableClassesFields(false)
  }

  function removeProps() {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < max; i++) {
      props.pop()
      i += 1
    }
  }

  function getPrebuiltClassesBySelectedOption(v: SelectOption | null) {
    return v === null
      ? []
      : prebuiltClasses.filter(
          (selectedOption) => selectedOption.name === v.label
        )[0].classSet
  }

  return (
    <>
      {prebuiltClasses.length !== 0 && (
        <AdvancedSelect
          name={name}
          id={name}
          value={value}
          onChange={(v: SelectOption | null) => onChangeSelectOption(v)}
          onBlur={() => setTouched(true)}
          options={prebuiltClasses ? prebuiltClasses.map(toOption) : []}
          error={!!(error && touched)}
          errorMessage={error}
          isDisabled={isLoading || isSubmitting}
          noOptionsMessage={() => t('No prebuilt classes set found')}
          placeholder={t('Select...')}
          defaultValue={initialValue}
          helpMessage={t(
            'Optionally a set of prebuilt classes can be selected above.'
          )}
          isClearable
        />
      )}
      <div />
    </>
  )
}

export default PrebuiltClassesSelect
